import React, { Fragment, useEffect, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
} from "@mui/material";
import { Tabs, Tab, InputAdornment } from "@mui/material";
import { Card, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../components/Admin/Reusable/Breadcrumb";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import {
  createNewChannel,
  fetchChannelById,
  fetchChannelFormList,
  updateChannel,
} from "../actions/channel";
import SubmitButtonForm from "../components/Admin/Reusable/Form/SubmitButtonForm";
import FormTitleMain from "../components/Admin/Reusable/FormTitleMain";
import InputField from "../components/Admin/Reusable/InputField";
import AmountInputField from "../components/Admin/Reusable/AmountInputField";
import UploadImage from "../components/Admin/Reusable/UploadImage";
import languagesItems from "../utils/languages";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import { Editor } from "ckeditor5-custom-build/build/ckeditor";

import { TabPanel, a11yProps } from "../helper/TabPanel";
import SelectStatus from "../components/Admin/Reusable/Form/SelectStatus";

import TagAutocomplete from "../components/Admin/Reusable/TagAutocomplete";
import AlertBox from "../components/Admin/Reusable/AlertBox";
import { useSelector } from "react-redux";
import {
  basicFormValidationHandler,
  formatOnlyDateDB,
  validateLocalesFields,
} from "../utils";
import { fetchUserFormList } from "../actions/user";
import { appendIfFile } from "../helper/FileCheck";
import { fetchCategoryFormList } from "../actions/category";
import { channelActions } from "../reducers/channel";
import GlobalProgressOverlay from "../components/Admin/Reusable/GlobalProgressOverlay";

const ChannelAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();

  const [activeLocaleTab, setActiveLocaleTab] = useState(0);
  const handleChangeLocaleTab = (e, newValue) => setActiveLocaleTab(newValue);
  const [errors, setErrors] = useState({});
  const { channelDetail, loading, success, actionType } = useSelector(
    (state) => state.channelReducer
  );
  const { userFormList } = useSelector((state) => state.userReducer);
  const { categoryFormList } = useSelector((state) => state.categoryReducer);

  //locales
  // const [locales, setLocales] = useState(() =>
  //   Object.fromEntries(
  //     languagesItems.map((l) => [
  //       l.code,
  //       { id: null, name: "", description: "", language: l.code },
  //     ])
  //   )
  // );
  const getInitialLocales = () =>
    Object.fromEntries(
      languagesItems.map((l) => [
        l.code,
        { id: null, name: "", description: "", language: l.code },
      ])
    );

  const [locales, setLocales] = useState(getInitialLocales);

  const categoryOptions = Array.isArray(categoryFormList)
    ? categoryFormList
    : [];

  const initialState = {
    price: "",
    discountPrice: "",
    discountStart: "",
    discountEnd: "",
    shoppable: true,
    discount: false,
    free: false,
    coverVideo: "",
    active: true,
    trending: false,
    ownerId: 0,
    categories: [],
    accessDuration: 30,
    position: 1,
    coverVideoTargetId: "",
    coverVideoPlayerId: "",
  };

  //osnovi podaci
  const [data, setData] = useState(initialState);
  const [progress, setProgress] = useState(0);
  const [showLoader, setShowLoader] = useState(false);

  const initialStateFiles = {
    banner: null,
    // mobileBanner: null,
    // coverBanner: null,
  };

  //fajlovi
  const [files, setFiles] = useState(initialStateFiles);

  //breadcrumbs
  const translateForm = "channelForm";
  const translateModule = "channel";
  const pageType = id ? "Edit" : "Add";
  const breadcrumbItems = [
    { label: t(`breadcrumb.${translateModule}`) },
    {
      label: t(`breadcrumb.${translateModule}${pageType}`),
      active: true,
    },
  ];

  const setFile = (key) => (file) =>
    setFiles((prev) => ({ ...prev, [key]: file }));

  useEffect(() => {
    dispatch(fetchUserFormList());
    dispatch(fetchCategoryFormList());
    if (!id) {
      // Ako `id` ne postoji, očisti stanje za novu kreaciju
      setData(initialState);
      setFiles(initialStateFiles);
      dispatch(fetchChannelFormList());
      setLocales(getInitialLocales());
    } else {
      // Ako `id` postoji, učitaj podatke za korisnika
      dispatch(fetchChannelById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (channelDetail && id) {
      setData({
        ...channelDetail,
        discountEnd: formatOnlyDateDB(channelDetail?.discountEnd),
        discountStart: formatOnlyDateDB(channelDetail?.discountStart),
      });
      setFiles({
        banner: channelDetail?.banner,
        // mobileBanner: channelDetail?.mobileBanner,
        // coverBanner: channelDetail?.coverBanner,
      });

      // popuni locales iz backend-a
      const filledLocales = Object.fromEntries(
        languagesItems.map((l) => {
          const found = channelDetail.locales?.find(
            (loc) =>
              loc.language === l.code || loc.language === l.label.toUpperCase()
          );
          return [
            l.code,
            {
              id: found?.id || null,
              name: found?.name || "",
              description: found?.description || "",
              language: l.code,
            },
          ];
        })
      );

      setLocales(filledLocales);
    }
  }, [channelDetail, id, dispatch]);

  useEffect(() => {
    let interval;
    let timeout;

    if (loading) {
      setShowLoader(true);
      setProgress(0);

      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 5;
        });
      }, 100);
    } else if (!loading && showLoader) {
      setProgress(100);
      timeout = setTimeout(() => {
        setShowLoader(false);
        setProgress(0);
      }, 2000);
    }

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [loading]);

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => {
        navigate("/channel-list");

        if (actionType === "create") {
          toast.success("Uspešno ste kreirali novi kanal", {
            theme: "colored",
          });
        } else if (actionType === "update") {
          toast.success("Uspešno ste izmenili kanal", { theme: "colored" });
        }

        dispatch(channelActions.clearError());
      }, 2000);

      return () => clearTimeout(timeout);
    } else {
      dispatch(channelActions.clearError());
    }
  }, [success]);

  const userValidationHandler = () => {
    let errorsObject = {};

    // univerzalno obavezna polja
    errorsObject = {
      ...errorsObject,
      ...basicFormValidationHandler({
        coverVideoTargetId: data.coverVideoTargetId,
        accessDuration: data.accessDuration,
        // ovde možeš dodati još uvek-obaveznih polja
      }),
    };

    // price je obavezan samo ako je shoppable === true
    if (data.shoppable) {
      errorsObject = {
        ...errorsObject,
        ...basicFormValidationHandler({
          price: data.price,
        }),
      };
    }

    // discount polja su obavezna samo ako je discount === true
    if (data.discount) {
      errorsObject = {
        ...errorsObject,
        ...basicFormValidationHandler({
          discountPrice: data.discountPrice,
          discountStart: data.discountStart,
        }),
      };
    }

    // ownerId je uvek obavezan
    if (!data.ownerId || data.ownerId === 0) {
      errorsObject.ownerId = "Obavezno polje";
    }

    const localeErrors = validateLocalesFields(locales, [
      "name",
      "description",
    ]);
    if (Object.keys(localeErrors).length > 0) {
      errorsObject.locales = localeErrors;
    }
    setErrors(errorsObject);

    if (!isEmpty(errorsObject)) {
      toast.error("Morate popuniti sva obavezna polja!", {
        theme: "colored",
      });
    }

    return !isEmpty(errorsObject);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (userValidationHandler()) {
      return;
    }

    // ✅ 2. Provera da li je slika odabrana
    if (!files.banner) {
      toast.error("Morate odabrati sliku.", { theme: "colored" });
      return;
    }

    const formData = new FormData();

    // pretvaranje locales objekta u niz za backend
    const localesArray = Object.entries(locales).map(([code, v]) => ({
      ...v,
      // ako postoji id, prosledi; ako ne, backend može generisati
      id: v.id || undefined,
    }));

    const dataForm = {
      ...data,
      locales: localesArray,
      categories: (data.categories || []).map((c) => c.id),
    };

    if (id) {
      dataForm.id = id;
    }

    // Ukloni polja za popust po difoltu
    delete dataForm.discountPrice;
    delete dataForm.discountStart;
    delete dataForm.discountEnd;

    // Ako je popust aktivan, DODAJ ih
    if (data.discount) {
      if (data.discountPrice !== "") {
        dataForm.discountPrice = data.discountPrice;
      }
      if (data.discountStart) {
        const d1 = new Date(data.discountStart);
        if (!isNaN(d1)) dataForm.discountStart = d1.toISOString();
      }
      if (data.discountEnd) {
        const d2 = new Date(data.discountEnd);
        if (!isNaN(d2)) dataForm.discountEnd = d2.toISOString();
      }
    }

    formData.append(
      "channel",
      new Blob([JSON.stringify(dataForm)], { type: "application/json" })
    );

    appendIfFile(formData, "banner", files.banner);
    // appendIfFile(formData, "mobileBanner", files.mobileBanner);
    // appendIfFile(formData, "coverBanner", files.coverBanner);

    if (id) {
      dispatch(updateChannel(formData, navigate, id));
    } else {
      dispatch(createNewChannel(formData, navigate));
    }
  };

  const updateLocale = (code, field, value) => {
    setLocales((prev) => ({
      ...prev,
      [code]: { ...prev[code], [field]: value },
    }));
  };

  const handleInputChange = (eOrName, maybeValue) => {
    if (typeof eOrName === "string") {
      const name = eOrName;
      const value = maybeValue;
      setData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    const e = eOrName;
    const { name, type, value, checked } = e.target;

    setData((prev) => {
      // default ažuriranje
      let next = { ...prev, [name]: type === "checkbox" ? checked : value };

      // pravila za međusobno isključive checkboxe
      if (type === "checkbox") {
        if (name === "shoppable") {
          if (checked) {
            next.free = false;
          } else {
            // ako više NIJE shoppable i NIJE free → none
            if (!next.free) {
              next.price = "";
              next.discount = false;
              next.discountPrice = "";
            }
          }
        }

        if (name === "free") {
          if (checked) {
            next.shoppable = false;
            // free režim: očisti cene/popuste
            next.price = "";
            next.discount = false;
            next.discountPrice = "";
          } else {
            // ako više NIJE free i NIJE shoppable → none
            if (!next.shoppable) {
              next.price = "";
              next.discount = false;
              next.discountPrice = "";
            }
          }
        }

        if (name === "discount") {
          // popust sme samo uz shoppable
          if (!next.shoppable && checked) {
            next.discount = false;
          }
          if (!checked) {
            // ako je isključen popust, očisti sve vrednosti vezane za popust
            next.discountPrice = "";
            next.discountStart = "";
            next.discountEnd = "";
          }
        }
      }

      return next;
    });
  };

  return (
    <Fragment>
      <Breadcrumb
        breadcrumbItems={breadcrumbItems}
        type={`${translateModule}${pageType}`}
        id={id}
        pageType={pageType}
      />
      {showLoader && <GlobalProgressOverlay progress={progress} />}
      <Container className="channel-container">
        <Row>
          <Col lg={12}>
            <form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col lg={12}>
                  <div className="text-end mb-2">
                    <SubmitButtonForm id={id} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={9}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>
                          <FormTitleMain />
                        </Card.Header>
                        <Card.Body>
                          <div className="blog-holder-admin-left-side-text">
                            <Row className="mb-2">
                              <Col>
                                <AlertBox
                                  title={
                                    "Ukoliko opcije 'Na prodaju' i 'Besplatno' nisu aktivne, korisnici ne mogu direktno da se pretplate na kanal. Umesto toga, kupovina master kanala automatski otključava pristup ovom kanalu (bez dodatne akcije korisnika)."
                                  }
                                />
                              </Col>
                            </Row>
                            <Row className="mb-2">
                              <Col>
                                <SelectStatus
                                  shoppable={true}
                                  free={true}
                                  data={data}
                                  row={true}
                                  handleInputChange={handleInputChange}
                                />
                              </Col>
                            </Row>
                            <Row>
                              {data?.shoppable && (
                                <Col lg={4} md={12} xs={12} className="mb-4">
                                  <AmountInputField
                                    label={t(`${translateForm}.price`)}
                                    name="price"
                                    value={data.price}
                                    onChange={handleInputChange}
                                    error={errors}
                                    errorsHandler={setErrors}
                                    fullWidth
                                    size="small"
                                    required={data?.shoppable}
                                    inputProps={{
                                      endAdornment: (
                                        <InputAdornment position="start">
                                          RSD
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Col>
                              )}
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <InputField
                                  label={t(
                                    `${translateForm}.coverVideoTargetId`
                                  )}
                                  name="coverVideoTargetId"
                                  value={data.coverVideoTargetId}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col>
                              {/* <Col lg={4} md={12} xs={12} className="mb-4">
                                <InputField
                                  label={t(
                                    `${translateForm}.coverVideoPlayerId`
                                  )}
                                  name="coverVideoPlayerId"
                                  value={data.coverVideoPlayerId}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col> */}
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <Autocomplete
                                  options={userFormList || []}
                                  getOptionLabel={(option) =>
                                    option
                                      ? `${option.firstName} ${option.surname}`
                                      : ""
                                  }
                                  value={
                                    userFormList?.find(
                                      (u) => u.id === data.ownerId
                                    ) || null
                                  }
                                  onChange={(e, newValue) => {
                                    handleInputChange(
                                      "ownerId",
                                      newValue ? newValue.id : 0
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={t(`${translateForm}.ownerId`)}
                                      size="small"
                                      error={!!errors.ownerId}
                                      helperText={errors.ownerId}
                                      required
                                    />
                                  )}
                                />
                              </Col>
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <InputField
                                  label={t(`${translateForm}.position`)}
                                  name="position"
                                  value={data.position}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  onChange={(e) => {
                                    const value = e.target.value;

                                    // Dozvoli samo cifre i max 6 karaktera
                                    if (
                                      /^\d*$/.test(value) &&
                                      value.length <= 6
                                    ) {
                                      // Ako je vrednost veća od 0 — prosledi je
                                      if (Number(value) > 0 || value === "") {
                                        handleInputChange(e);
                                      }
                                    }
                                  }}
                                  inputProps={{
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                  }}
                                />
                              </Col>
                              <Col lg={8} md={12} xs={12} className="mb-4">
                                {!data.categories ? (
                                  <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={40}
                                  />
                                ) : (
                                  <TagAutocomplete
                                    options={categoryOptions}
                                    label="Kategorije"
                                    value={data.categories}
                                    name="categories"
                                    onChangeHandler={handleInputChange}
                                  />
                                )}
                              </Col>
                              {data?.shoppable && (
                                <Col lg={4} md={12} xs={12} className="mb-4">
                                  <InputField
                                    label={t(`${translateForm}.accessDuration`)}
                                    name="accessDuration"
                                    value={data.accessDuration}
                                    onChange={handleInputChange}
                                    error={errors}
                                    errorsHandler={setErrors}
                                    fullWidth
                                    size="small"
                                    required={data?.shoppable}
                                  />
                                </Col>
                              )}
                            </Row>
                            <Row>
                              <Col lg={12}>
                                <Card>
                                  <Card.Header>
                                    <SelectStatus
                                      discount={true}
                                      data={data}
                                      handleInputChange={handleInputChange}
                                    />
                                  </Card.Header>
                                  {data?.discount && (
                                    <Card.Body>
                                      <Row>
                                        <Col
                                          lg={4}
                                          md={12}
                                          xs={12}
                                          className="mb-4"
                                        >
                                          <AmountInputField
                                            label={t(
                                              `${translateForm}.discountPrice`
                                            )}
                                            name="discountPrice"
                                            value={data.discountPrice}
                                            onChange={handleInputChange}
                                            error={errors}
                                            errorsHandler={setErrors}
                                            fullWidth
                                            required={data?.discount}
                                            size="small"
                                            inputProps={{
                                              endAdornment: (
                                                <InputAdornment position="start">
                                                  RSD
                                                </InputAdornment>
                                              ),
                                            }}
                                          />
                                        </Col>
                                        <Col
                                          lg={4}
                                          md={12}
                                          xs={12}
                                          className="mb-4"
                                        >
                                          <InputField
                                            type="date"
                                            label={t(
                                              `${translateForm}.discountStart`
                                            )}
                                            name="discountStart"
                                            value={data?.discountStart}
                                            onChange={handleInputChange}
                                            error={errors}
                                            errorsHandler={setErrors}
                                            fullWidth
                                            required={data?.discount}
                                            size="small"
                                            inputLabelProps={{ shrink: true }}
                                            inputProps={{
                                              max:
                                                data?.discountEnd || undefined,
                                            }}
                                          />
                                        </Col>
                                        <Col
                                          lg={4}
                                          md={12}
                                          xs={12}
                                          className="mb-4"
                                        >
                                          <InputField
                                            type="date"
                                            label={t(
                                              `${translateForm}.discountEnd`
                                            )}
                                            name="discountEnd"
                                            value={data?.discountEnd}
                                            onChange={handleInputChange}
                                            error={errors}
                                            errorsHandler={setErrors}
                                            fullWidth
                                            size="small"
                                            inputLabelProps={{ shrink: true }}
                                            inputProps={{
                                              min:
                                                data?.discountStart ||
                                                undefined,
                                            }}
                                          />
                                        </Col>
                                      </Row>
                                    </Card.Body>
                                  )}
                                </Card>
                              </Col>
                            </Row>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>
                          <FormTitleMain title="Prevodi" />
                        </Card.Header>
                        <Card.Body>
                          {/* Tabs header */}
                          <Tabs
                            value={activeLocaleTab}
                            onChange={handleChangeLocaleTab}
                            variant="scrollable"
                            // scrollButtons
                            // allowScrollButtonsMobile
                          >
                            {languagesItems.map((lng, idx) => (
                              <Tab
                                key={lng.code}
                                label={lng.label}
                                {...a11yProps(idx)}
                                // primer: pokaži zvezdicu ako fali naslov
                                iconPosition="end"
                              />
                            ))}
                          </Tabs>

                          {/* Tabs content */}
                          {languagesItems.map((lng, idx) => (
                            <TabPanel
                              key={lng.code}
                              value={activeLocaleTab}
                              index={idx}
                            >
                              <Row>
                                <Col lg={12} className="mb-4">
                                  <InputField
                                    name={`name_${lng.code}`}
                                    label={`${t(`${translateForm}.name`)} `}
                                    value={locales[lng.code].name}
                                    size="small"
                                    onChange={(e) =>
                                      updateLocale(
                                        lng.code,
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    error={errors?.locales?.[lng.code]?.name}
                                    errorsHandler={setErrors}
                                    required
                                  />
                                </Col>

                                <Col lg={12} className="mb-3">
                                  <label className="form-label">
                                    {t(`${translateForm}.description`)}
                                    <span className="text-danger"> *</span>
                                  </label>

                                  <CKEditor
                                    editor={Editor}
                                    data={locales[lng.code].description || ""}
                                    onChange={(event, editor) => {
                                      const data = editor.getData();
                                      updateLocale(
                                        lng.code,
                                        "description",
                                        data
                                      );
                                    }}
                                    onReady={(editor) => {
                                      // opciono ako želiš da editor bude full width
                                      editor.editing.view.change((writer) => {
                                        writer.setStyle(
                                          "min-height",
                                          "250px",
                                          editor.editing.view.document.getRoot()
                                        );
                                      });
                                    }}
                                  />

                                  {errors?.locales?.[lng.code]?.description && (
                                    <div className="text-danger mt-1">
                                      {errors.locales[lng.code].description}
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            </TabPanel>
                          ))}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
                <Col lg={3}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>{t(`global.settings`)}</Card.Header>
                        <Card.Body>
                          <SelectStatus
                            active={true}
                            trending={true}
                            data={data}
                            handleInputChange={handleInputChange}
                          />
                        </Card.Body>
                      </Card>
                    </Col>
                    {/* <Col lg={12}>
                      <Card>
                        <Card.Header>Cover slika</Card.Header>
                        <Card.Body>
                          <UploadImage
                            name="coverBanner"
                            label="Odaberite sliku"
                            value={files.coverBanner}
                            onChange={setFile("coverBanner")}
                            required
                          />
                        </Card.Body>
                      </Card>
                    </Col> */}
                    {/* <Col lg={12}>
                      <Card>
                        <Card.Header>Mobilna slika</Card.Header>
                        <Card.Body>
                          <UploadImage
                            name="mobileBanner"
                            label="Odaberite sliku"
                            value={files.mobileBanner}
                            onChange={setFile("mobileBanner")}
                            required
                          />
                        </Card.Body>
                      </Card>
                    </Col> */}
                    <Col lg={12}>
                      <Card>
                        <Card.Header>Slika</Card.Header>
                        <Card.Body>
                          <div className="image-info-detail">
                            <span>
                              <b>Dimenzija slike:</b> 300 x 375 px
                            </span>
                            <br />
                            <span>
                              <b>Format slike:</b> .jpg, .png, .jpeg, .gif,
                              .svg, .webp
                            </span>
                            <br />
                            <span>
                              <b>Maksimalna veličina fajla:</b> do 500 KB
                              (preporučeno), maksimalno 1 MB
                            </span>
                          </div>
                          <UploadImage
                            name="banner"
                            label="Odaberite sliku"
                            value={files.banner}
                            onChange={setFile("banner")}
                            required
                          />
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ChannelAdd;
